import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { usePoaFeeCandidates, useSelectableOffices, useWypisPoaFeeSuggestion } from "../../hooks";
import type { WypisOrder } from "../../types";
import { PoaFeeOptionGroups, UnpaidPoaFeeNote } from "../PoaFeeOptions";
import { PoaSuggestionHint } from "../PoaSuggestionHint";
import { StampDutyOfficePicker } from "../StampDutyOfficePicker";

/** Modal 17 zł opłaty skarbowej za pełnomocnictwo dla zamówienia wypisów — nowa /
 *  istniejąca / niepotrzebna, z sugestią AI (miasto mocodawcy + ≤7 dni + niepodpięta do
 *  innego wniosku do tego urzędu). Bliźniak modala z wniosków, ale per zamówienie.
 *  Zamówienie może mieć KILKA opłat (po jednej na mocodawcę) — modal DOPINA kolejną,
 *  nie podmienia. Urząd rozpoznany z pola 1 wniosku można tu poprawić ręcznie. */
export function WypisPoaFeeModal({
  order, onClose, onSubmit, pending,
}: {
  order: WypisOrder;
  onClose: () => void;
  onSubmit: (payload: { mode: "new" | "existing" | "not_required" | "set_office"; fee?: string;
                        reason?: string; office_account_number?: string;
                        stamp_duty_office?: string }) => void;
  pending: boolean;
}) {
  const duty = order.stamp_duty_office;
  const [mode, setMode] = useState<"new" | "existing" | "not_required">("new");
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState("");
  const [account, setAccount] = useState(duty.bank_account || "");
  // "" = rozpoznanie automatyczne; ustawione = urząd wskazany ręcznie w tym oknie.
  const [office, setOffice] = useState(duty.manual ? duty.office_id ?? "" : "");
  const [officeTouched, setOfficeTouched] = useState(false);

  const { data: offices } = useSelectableOffices(office);
  const pickedOffice = (offices ?? []).find((o) => o.id === office);
  const knownAccount = office ? pickedOffice?.bank_account || "" : duty.bank_account;

  const { data: candidates } = usePoaFeeCandidates(true);
  const { data: suggestion, isPending: suggestionLoading } = useWypisPoaFeeSuggestion(order.id, true);
  const applySuggestion = () => {
    if (suggestion?.mode === "existing" && suggestion.fee) {
      setMode("existing");
      setFee(suggestion.fee.id);
    } else {
      setMode("new");
    }
  };
  const [applied, setApplied] = useState(false);
  useEffect(() => {
    if (!suggestion || applied) return;
    setApplied(true);
    applySuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion, applied]);
  const suggestedFeeId = suggestion?.mode === "existing" ? suggestion.fee?.id : undefined;

  // Opłaty już podpięte do zamówienia odpadają z listy — modal dopina KOLEJNĄ.
  const linkedIds = new Set(order.poa_fees.map((f) => f.id));
  const feeOptions = (() => {
    const byId = new Map((candidates ?? []).map((c) => [c.id, c]));
    for (const c of suggestion?.candidates ?? []) if (!byId.has(c.id)) byId.set(c.id, c);
    return [...byId.values()].filter((c) => !linkedIds.has(c.id));
  })();

  const changeOffice = (value: string) => {
    setOffice(value);
    setOfficeTouched(true);
  };

  return (
    <Modal open onClose={onClose} title="Opłata skarbowa 17 zł — pełnomocnictwo">
      <div className="space-y-3 text-xs">
        <StampDutyOfficePicker
          info={duty} value={office} onChange={changeOffice} disabled={pending}
          onSaveOnly={() => onSubmit({ mode: "set_office", stamp_duty_office: office })}
        />
        {duty.mixed_clients && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-amber-700 dark:text-amber-400">
            Uwaga: działki zamówienia należą do kilku klientów — mocodawcę przyjęto z
            projektu z największą liczbą działek. Sprawdź, czy urząd opłaty jest właściwy.
          </div>
        )}
        <PoaSuggestionHint
          loading={suggestionLoading}
          suggestion={suggestion}
          showApply={!!suggestion && (mode !== suggestion.mode
            || (suggestion.mode === "existing" && fee !== suggestedFeeId))}
          onApply={applySuggestion}
        />
        <div className="space-y-1">
          {([
            ["new", "Poproś o nową opłatę (obieg /official-fees)"],
            ["existing", "Podepnij istniejącą opłatę „za pełnomocnictwo”"],
            ["not_required", "Opłata niepotrzebna (z uzasadnieniem)"],
          ] as const).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input type="radio" checked={mode === value} onChange={() => setMode(value)} />
              {label}
            </label>
          ))}
        </div>
        {mode === "new" && !knownAccount && (
          <label className="block">
            <span className="section-label">Numer konta urzędu opłaty *</span>
            <input className="input-base mt-1 font-mono" placeholder="26 cyfr (PL…)"
                   value={account} onChange={(e) => setAccount(e.target.value)} />
          </label>
        )}
        {mode === "existing" && (
          <label className="block">
            <span className="section-label">Opłata</span>
            <select className="input-base mt-1" value={fee} onChange={(e) => setFee(e.target.value)}>
              <PoaFeeOptionGroups options={feeOptions} suggestedFeeId={suggestedFeeId} />
            </select>
            <UnpaidPoaFeeNote
              fee={feeOptions.find((c) => c.id === fee)}
              consequence="Możesz ją podpiąć — data opłacenia i potwierdzenie przelewu pojawią się przy zamówieniu, gdy opłata zostanie opłacona w /official-fees."
            />
          </label>
        )}
        {mode === "not_required" && (
          <label className="block">
            <span className="section-label">Uzasadnienie *</span>
            <input className="input-base mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
            {order.poa_fees.length > 0 && (
              <span className="block mt-1 text-amber-700 dark:text-amber-400">
                Podpięte opłaty ({order.poa_fees.length}) zostaną odpięte od zamówienia
                (pozostaną w /official-fees).
              </span>
            )}
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm" loading={pending}
            disabled={(mode === "existing" && !fee) || (mode === "not_required" && !reason)}
            onClick={() => onSubmit({
              mode, fee: fee || undefined, reason: reason || undefined,
              office_account_number: account || undefined,
              ...(officeTouched ? { stamp_duty_office: office } : {}),
            })}
          >
            Zapisz
          </Button>
        </div>
      </div>
    </Modal>
  );
}
